"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const faqs = [
  {
    question: "How do I register as a seller on the platform?",
    answer:
'To register as a seller, <a href="/auth/register" style="color:#002301;font-weight:600;">get started here</a> and select "Register as Seller". You will need to provide your business details, product information, and undergo verification. Once approved, you can start listing your solar products on our marketplace.',
  },
  {
    question: "What types of solar products can I sell on this platform?",
    answer:
      "You can sell a wide range of solar products including solar panels, inverters, batteries, solar water heaters, mounting systems, and accessories. We also support solar service providers and recyclers in our marketplace.",
  },
  {
    question: "What support is available for new users?",
    answer:
      "New users receive comprehensive onboarding support including step-by-step guides, video tutorials, and dedicated customer support. Our team is available to help with account setup, product listings, and any technical issues you may encounter.",
  },
  {
    question: "How secure is the transaction process?",
    answer:
      "All transactions are secured with industry-standard encryption and payment processing. We offer buyer protection policies and verified seller verification to ensure safe and trustworthy transactions.",
  },
  {
    question: "Can homeowners request installation through the platform?",
    answer:
'Yes. Homeowners can browse verified installers and service providers in their area, request quotes, and schedule <a href="/consultation" style="color:#002301;font-weight:600;">a consultation</a> directly through the platform. Our team also offers private consultation services for personalised guidance.',
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const half = Math.ceil(faqs.length / 2);
  const leftFaqs = faqs.slice(0, half);
  const rightFaqs = faqs.slice(half);

  const FaqItem = ({ faq, index }: { faq: (typeof faqs)[0]; index: number }) => (
    <div className="bg-white overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 rounded-xl mb-3">
      <button
        onClick={() => setOpenIndex(openIndex === index ? null : index)}
        className="w-full px-5 py-4 flex items-center justify-between text-left focus:outline-none"
      >
        <span className="text-sm font-semibold text-gray-900 pr-6 leading-snug">
          {faq.question}
        </span>
        <ChevronDown
          className={cn(
            "w-5 h-5 text-[#002301] transition-transform duration-300 flex-shrink-0",
            openIndex === index && "rotate-180"
          )}
        />
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-500 ease-in-out",
          openIndex === index ? "max-h-96" : "max-h-0"
        )}
      >
        <div className="px-5 pb-5 pt-1">
          <p className="text-gray-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
        </div>
      </div>
    </div>
  );

  return (
    <section className="bg-[#F8FAFC] py-16 sm:py-20 px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
          >
            Your questions are answered here
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
          >
            Frequently Asked Questions
          </motion.h2>
        </div>

        {/* Two-column FAQ grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
          <div>
            {leftFaqs.map((faq, i) => (
              <FaqItem key={i} faq={faq} index={i} />
            ))}
          </div>
          <div>
            {rightFaqs.map((faq, i) => (
              <FaqItem key={i + half} faq={faq} index={i + half} />
            ))}
          </div>
        </div>

        {/* Support link */}
        <div className="text-center mt-10">
          <p className="text-gray-500 text-sm">
            Still have questions?{" "}
            <Link
              href="/faqs"
              className="text-[#002301] font-semibold hover:text-[#17BC2E] underline underline-offset-2 transition-colors"
            >
              View all FAQs
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
